import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Link, CheckCircle, AlertCircle, ExternalLink, Crown } from 'lucide-react';
import { ocrService, OCRResult } from '@/services/ocrService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface WebLinkSectionProps {
  onQuestionsExtracted: (result: OCRResult) => void;
  subject?: string;
  onSubjectChange?: (subject: string) => void;
  disabled?: boolean;
}

const WebLinkSection: React.FC<WebLinkSectionProps> = ({
  onQuestionsExtracted,
  subject = '',
  onSubjectChange,
  disabled = false
}) => {
  const [webUrl, setWebUrl] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [localSubject, setLocalSubject] = useState(subject);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const { toast } = useToast();
  const { hasAIAccess } = useAuth();

  // Check if user has tier 2 access
  if (!hasAIAccess('advanced')) {
    return (
      <Card data-id="ts1ospxre" data-path="src/components/questions/WebLinkSection.tsx">
        <CardContent className="pt-6" data-id="75ykd4y7t" data-path="src/components/questions/WebLinkSection.tsx">
          <div className="text-center space-y-4" data-id="hatyxkf8f" data-path="src/components/questions/WebLinkSection.tsx">
            <Crown className="h-12 w-12 mx-auto text-amber-500" data-id="7tvnbuxq5" data-path="src/components/questions/WebLinkSection.tsx" />
            <h3 className="text-lg font-semibold" data-id="pdj64pjnz" data-path="src/components/questions/WebLinkSection.tsx">Premium Feature</h3>
            <p className="text-gray-600" data-id="baw1q7si6" data-path="src/components/questions/WebLinkSection.tsx">
              Extract questions from web links is available for Tier 2 users only. Contact your administrator to upgrade your tier.
            </p>
          </div>
        </CardContent>
      </Card>);

  }

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleProcessWebLink = async () => {
    if (!webUrl.trim()) {
      toast({
        title: "URL Required",
        description: "Please enter a web URL to process.",
        variant: "destructive"
      });
      return;
    }

    if (!isValidUrl(webUrl)) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid web URL (including http:// or https://).",
        variant: "destructive"
      });
      return;
    }

    if (!localSubject.trim()) {
      toast({
        title: "Subject Required",
        description: "Please enter a subject for the questions.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    setProgress(10);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const result = await ocrService.processWebLink(webUrl, localSubject, difficulty);

      clearInterval(progressInterval);
      setProgress(100);

      toast({
        title: "Questions Extracted Successfully",
        description: `Generated ${result.questions.length} questions from web content`
      });

      onQuestionsExtracted(result);

      // Reset form
      setWebUrl('');
      setProgress(0);

    } catch (error) {
      console.error('Error processing web link:', error);
      toast({
        title: "Processing Failed",
        description: error instanceof Error ? error.message : "Failed to process web content",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  return (
    <Card data-id="jt8tga5fa" data-path="src/components/questions/WebLinkSection.tsx">
      <CardHeader data-id="436jhobp6" data-path="src/components/questions/WebLinkSection.tsx">
        <CardTitle className="flex items-center gap-2" data-id="br88spvio" data-path="src/components/questions/WebLinkSection.tsx">
          <Link className="h-5 w-5" data-id="pdkckw5x2" data-path="src/components/questions/WebLinkSection.tsx" />
          <Crown className="h-4 w-4 text-amber-500" data-id="0kzxo6fif" data-path="src/components/questions/WebLinkSection.tsx" />
          Extract Questions from Web Link
        </CardTitle>
        <CardDescription data-id="w1hsk6i84" data-path="src/components/questions/WebLinkSection.tsx">
          Enter a web URL containing educational content, and AI will extract questions from the page content. (Tier 2 feature)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4" data-id="m9vt141n9" data-path="src/components/questions/WebLinkSection.tsx">
        {/* Subject and Difficulty Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="vpn7wagbf" data-path="src/components/questions/WebLinkSection.tsx">
          <div className="space-y-2" data-id="z6iglojbo" data-path="src/components/questions/WebLinkSection.tsx">
            <Label htmlFor="web-subject" data-id="muns91i2s" data-path="src/components/questions/WebLinkSection.tsx">Subject</Label>
            <Input
              id="web-subject"
              value={localSubject}
              onChange={(e) => {
                setLocalSubject(e.target.value);
                onSubjectChange?.(e.target.value);
              }}
              placeholder="e.g., Mathematics, Science, History"
              disabled={isProcessing || disabled} data-id="zzrre9hez" data-path="src/components/questions/WebLinkSection.tsx" />

          </div>
          <div className="space-y-2" data-id="021ov4t4v" data-path="src/components/questions/WebLinkSection.tsx">
            <Label htmlFor="web-difficulty" data-id="s033e48ft" data-path="src/components/questions/WebLinkSection.tsx">Difficulty Level</Label>
            <Select
              value={difficulty}
              onValueChange={(value: 'easy' | 'medium' | 'hard') => setDifficulty(value)}
              disabled={isProcessing || disabled} data-id="s8n5v9vfg" data-path="src/components/questions/WebLinkSection.tsx">
              <SelectTrigger data-id="mt0n1gww8" data-path="src/components/questions/WebLinkSection.tsx">
                <SelectValue data-id="mkgs5vcqy" data-path="src/components/questions/WebLinkSection.tsx" />
              </SelectTrigger>
              <SelectContent data-id="sxrfqh6kd" data-path="src/components/questions/WebLinkSection.tsx">
                <SelectItem value="easy" data-id="yhc37qltf" data-path="src/components/questions/WebLinkSection.tsx">Easy</SelectItem>
                <SelectItem value="medium" data-id="bluwofuw7" data-path="src/components/questions/WebLinkSection.tsx">Medium</SelectItem>
                <SelectItem value="hard" data-id="d0qbjxshn" data-path="src/components/questions/WebLinkSection.tsx">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Web URL Input */}
        <div className="space-y-2" data-id="ejea59k4r" data-path="src/components/questions/WebLinkSection.tsx">
          <Label htmlFor="web-url" data-id="3esvqbyf0" data-path="src/components/questions/WebLinkSection.tsx">Web URL</Label>
          <div className="relative" data-id="6xoyg0pag" data-path="src/components/questions/WebLinkSection.tsx">
            <ExternalLink className="absolute left-3 top-3 h-4 w-4 text-gray-400" data-id="4l2wpl3zl" data-path="src/components/questions/WebLinkSection.tsx" />
            <Input
              id="web-url"
              type="url"
              value={webUrl}
              onChange={(e) => setWebUrl(e.target.value)}
              placeholder="https://example.com/educational-content"
              className="pl-10"
              disabled={isProcessing || disabled} data-id="1bedwkbr0" data-path="src/components/questions/WebLinkSection.tsx" />

          </div>
        </div>

        {/* Processing Progress */}
        {isProcessing &&
        <div className="space-y-2" data-id="cqkq040km" data-path="src/components/questions/WebLinkSection.tsx">
            <div className="flex justify-between text-sm" data-id="203b931kt" data-path="src/components/questions/WebLinkSection.tsx">
              <span data-id="gpkurt52v" data-path="src/components/questions/WebLinkSection.tsx">Processing web content...</span>
              <span data-id="q57aqeo6c" data-path="src/components/questions/WebLinkSection.tsx">{progress}%</span>
            </div>
            <Progress value={progress} className="w-full" data-id="yfli7948j" data-path="src/components/questions/WebLinkSection.tsx" />
          </div>
        }

        {/* Process Button */}
        <Button
          onClick={handleProcessWebLink}
          disabled={!webUrl.trim() || isProcessing || !localSubject.trim() || !isValidUrl(webUrl) || disabled}
          className="w-full" data-id="0bhfnd3tn" data-path="src/components/questions/WebLinkSection.tsx">
          {isProcessing ?
          <>
              <AlertCircle className="h-4 w-4 mr-2 animate-spin" data-id="spjblg87w" data-path="src/components/questions/WebLinkSection.tsx" />
              Processing Web Content...
            </> :
          <>
              <CheckCircle className="h-4 w-4 mr-2" data-id="k98t8c4ed" data-path="src/components/questions/WebLinkSection.tsx" />
              Extract Questions
            </>
          }
        </Button>

        {/* Help Text */}
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg" data-id="e9kbc3vuu" data-path="src/components/questions/WebLinkSection.tsx">
          <strong data-id="y9p5jcxlh" data-path="src/components/questions/WebLinkSection.tsx">Tips for better results:</strong>
          <ul className="mt-1 space-y-1 list-disc list-inside" data-id="wrucpk8xq" data-path="src/components/questions/WebLinkSection.tsx">
            <li data-id="xxsbpddzi" data-path="src/components/questions/WebLinkSection.tsx">Use URLs with educational content like articles, tutorials, or study materials</li>
            <li data-id="9nhytl89l" data-path="src/components/questions/WebLinkSection.tsx">Ensure the webpage content is publicly accessible</li>
            <li data-id="k70b4um3o" data-path="src/components/questions/WebLinkSection.tsx">Pages with structured content produce better question extraction</li>
            <li data-id="mv4vhdvs6" data-path="src/components/questions/WebLinkSection.tsx">AI will analyze the text content and generate relevant questions</li>
          </ul>
        </div>

        {/* Supported URL Examples */}
        <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg" data-id="hra3bhs5u" data-path="src/components/questions/WebLinkSection.tsx">
          <strong data-id="xqhihm9nt" data-path="src/components/questions/WebLinkSection.tsx">Supported content types:</strong>
          <ul className="mt-1 space-y-1 list-disc list-inside" data-id="2mk4isjjz" data-path="src/components/questions/WebLinkSection.tsx">
            <li data-id="xpn6mrzem" data-path="src/components/questions/WebLinkSection.tsx">Educational articles and blog posts</li>
            <li data-id="ux71f49xx" data-path="src/components/questions/WebLinkSection.tsx">Online textbook chapters</li>
            <li data-id="4vlchjdhs" data-path="src/components/questions/WebLinkSection.tsx">Wikipedia articles</li>
            <li data-id="e5ca8pfad" data-path="src/components/questions/WebLinkSection.tsx">Educational resource websites</li>
            <li data-id="btveq2etr" data-path="src/components/questions/WebLinkSection.tsx">Academic papers (if publicly accessible)</li>
          </ul>
        </div>
      </CardContent>
    </Card>);

};

export default WebLinkSection;